import axiosInstance from 'services/common/axiosInstance';

export interface Course {
  id: number;
  course_name: string;
  module: number;
  description: string;
  duration: number;
  course_level: string;
  trainer: string;
  thumbnail_url: string;
  remarks: string;
  status?: string;
  createdon: string;
  price: number;
  createdby: string;
  updatedon: string | null;
  updatedby: string | null;
}
export interface Pagination {
  page: number;
  per_page: number;
  total_pages: number;
  total_items: number;
}

export interface CourseListResponse {
  message: string;
  data: {
    courses: Course[];
    pagination: Pagination;
  };
  success: boolean;
}

export interface UpdateCourseResponse {
  message: string;
  data: number;
  success: boolean;
}

export interface AddCourseParams {
  course_name: string;
  description: string;
  duration: number;
  course_level: string;
  trainer: string;
  thumbnail_url: string;
  remarks: string;
  price: number;
  module: number;
}

export interface UpdateCourseParams extends AddCourseParams {}

export interface GetCourseListParams {
  page: number;
  per_page: number;
  from_date: string;
  to_date: string;
  course_id: number;
  searchkey: string;
  status?: number;
}
export interface Skill {
  id: number;
  course_id: number;
  skill_name: string;
  status: string;
  createdon: string;
}

export interface AddSkillParams {
  course_id: number;
  skill_name: string;
}

export interface UpdateSkillParams {
  course_id: number;
  skill_name: string;
}

export interface SkillListResponse {
  message: string;
  data: Skill[];
  success: boolean;
}

export interface SkillActionResponse {
  message: string;
  data: number; 
  success: boolean;
}

export interface KeySummary {
  id: number;
  course_id: number;
  head_line: string;
  keysummary: string;
  status: string;
  image_path: string;
  createdon: string;
}

export interface AddKeySummaryParams {
  course_id: number;
  head_line: string;
  keysummary: string;
  image_path: string;
}

export interface UpdateKeySummaryParams {
  course_id: number;
  head_line: string;
  keysummary: string;
  image_path: string;
}

export interface ListKeySummaryResponse {
  message: string;
  data: KeySummary[];
  success: boolean;
}

export interface KeySummaryActionResponse {
  message: string;
  data: number;  
  success: boolean;
}
export interface SyllabusDetailDay {
  dayname: string;
  title: string | null;
  description: string | null;
}

export interface SyllabusWeek {
  weekname: string;
  title: string | null;
  keydeliverable: string;
  detail: SyllabusDetailDay[];
}

export interface SyllabusDetailListResponse {
  message: string;
  data: SyllabusDetailDay[];
  success: boolean;
}
export interface SyllabusItem {
  key_deliverables: string;
  daywise_description: string;
  syllabusdetail: SyllabusWeek[];
  status: string;
  createdon: string;
}

export interface ListSyllabusResponse {
  message: string;
  data: SyllabusItem[];
  success: boolean;
}

export interface AddSyllabusParams {
  course_id: number;
  key_deliverables: string;
  daywise_description: string;
}

export interface UpdateSyllabusParams {
  course_id: number;
  key_deliverables: string;
  daywise_description: string;
  status: string;
}

export interface SyllabusActionResponse {
  message: string;
  data: number; 
  success: boolean;
}

export interface AddSyllabusDetailParams {
  weekdetail_id: number;
  dayid: number;
  title: string;
  Description: string;
}

export interface SyllabusDetailActionResponse {
  message: string;
  data: number;
  success: boolean;
}

export interface AddWeeklySyllabusParams {
  weekid: number;
  syllabus_id: number;
  title: string;
  keydeliverable: string;
}

export interface WeeklySyllabusActionResponse {
  message: string;
  data: number;
  success: boolean;
}
export interface WeekItem {
  id: number;
  weekname: string;
  status: string;
}

export interface WeekListResponse {
  message: string;
  data: WeekItem[];
  success: boolean;
}
export interface DayItem {
  id: number;
  dayname: string;
  status: string;
}

export interface DayListResponse {
  message: string;
  data: DayItem[];
  success: boolean;
}

export const fetchCourseList = async (
  data: GetCourseListParams
): Promise<CourseListResponse> => {
  try {
    const response = await axiosInstance.post('/course/listcourseforadmin', data);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};

export const addCourse = async (
  data: AddCourseParams
): Promise<UpdateCourseResponse> => {
  try {
    const response = await axiosInstance.post('/course/addcourse', data);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};

export const updateCourse = async (
  course_id: number,
  data: UpdateCourseParams
): Promise<UpdateCourseResponse> => {
  try {
    const response = await axiosInstance.put(`/course/updatecourse?course_id=${course_id}`, data);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};

export interface UpdateCourseStatusParams {
  status: string;
}

export const updateCourseStatus = async (
  course_id: number,
  data: UpdateCourseStatusParams
): Promise<UpdateCourseResponse> => {
  try {
    const response = await axiosInstance.put(
      `/course/updatecoursestatus?course_id=${course_id}`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};

export const deleteCourse = async (course_id: number): Promise<UpdateCourseResponse> => {
  try {
    const response = await axiosInstance.delete(`/course/deletecourse?course_id=${course_id}`);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};

export const addSkill = async (
  data: AddSkillParams
): Promise<SkillActionResponse> => {
  try {
    const response = await axiosInstance.post("/course/addskill", data);
    return response.data; 
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};

export const listSkill = async (
  course_id: number
): Promise<SkillListResponse> => {
  try {
    const response = await axiosInstance.get(
      `/course/listskill?course_id=${course_id}`
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};

export const updateSkill = async (
  skill_id: number,
  data: UpdateSkillParams
): Promise<SkillActionResponse> => {
  try {
    const response = await axiosInstance.put(
      `/course/updateskill?skill_id=${skill_id}`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};

export const deleteSkill = async (
  skill_id: number
): Promise<SkillActionResponse> => {
  try {
    const response = await axiosInstance.delete(
      `/course/deleteskill?skill_id=${skill_id}`
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};

export const addKeySummary = async (
  data: AddKeySummaryParams
): Promise<KeySummaryActionResponse> => {
  try {
    const response = await axiosInstance.post("/course/addkeysummary", data);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};

export const listKeySummary = async (
  course_id: number
): Promise<ListKeySummaryResponse> => {
  try {
    const response = await axiosInstance.get(
      `/course/listkeysummary?course_id=${course_id}`
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};

export const updateKeySummary = async (
  keysummary_id: number,
  data: UpdateKeySummaryParams
): Promise<KeySummaryActionResponse> => {
  try {
    const response = await axiosInstance.put(
      `/course/updatekeysummary?keysummary_id=${keysummary_id}`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};

export const deleteKeySummary = async (
  keysummary_id: number
): Promise<KeySummaryActionResponse> => {
  try {
    const response = await axiosInstance.delete(
      `/course/deletekeysummary?keysummary_id=${keysummary_id}`
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};

export const addSyllabus = async (
  data: AddSyllabusParams
): Promise<SyllabusActionResponse> => {
  try {
    const response = await axiosInstance.post("/course/addsyllabus", data);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};

export const updateSyllabus = async (
  syllabus_id: number,
  data: UpdateSyllabusParams
): Promise<SyllabusActionResponse> => {
  try {
    const response = await axiosInstance.put(
      `/course/updatesyllabus?syllabus_id=${syllabus_id}`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};

export const deleteSyllabus = async (
  syllabus_id: number
): Promise<SyllabusActionResponse> => {
  try {
    const response = await axiosInstance.delete(
      `/course/deletesyllabus?syllabus_id=${syllabus_id}`
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};

export const listSyllabus = async (
  course_id: number
): Promise<ListSyllabusResponse> => {
  try {
    const response = await axiosInstance.get(
      `/course/listsyllabus?course_id=${course_id}`
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};

export const listSyllabusDetail = async (
  syllabus_id: number
): Promise<SyllabusDetailListResponse> => {
  try {
    const response = await axiosInstance.get(
      `/course/syllabusdetaillist?syllabus_id=${syllabus_id}`
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};
export const addSyllabusDetail = async (
  data: AddSyllabusDetailParams
): Promise<SyllabusDetailActionResponse> => {
  try {
    const response = await axiosInstance.post("/course/addsyllabusdetail", data);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};

export const addWeeklySyllabus = async (
  data: AddWeeklySyllabusParams
): Promise<WeeklySyllabusActionResponse> => {
  try {
    const response = await axiosInstance.post("/course/addweeklysyllabus", data);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};
export const fetchWeekList = async (): Promise<WeekListResponse> => {
  try {
    const response = await axiosInstance.get("/course/weeklist");
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};
export const fetchDayList = async (): Promise<DayListResponse> => {
  try {
    const response = await axiosInstance.get("/course/daylist");
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error.message;
  }
};